import React, { useEffect, useMemo, useState } from "react";
import {
  Briefcase20Regular,
  Note20Regular,
  Calendar20Regular,
  Mail20Regular,
  Call20Regular,
  PeopleSwap20Regular,
} from "@fluentui/react-icons";
import { vorgangService, type VorgangDto, type TodoDto } from "../services/vorgangService";
import { apiFetch } from "../utils/apiFetch";
import { useReferenceData } from "../context/ReferenceDataContext";

type EntryType =
  | "vorgang"
  | "todo"
  | "termin"
  | "mail"
  | "anruf"
  | "zusammenfuehrung"
  | "verantwortlichkeit";

type Entry = {
  id: string;
  date: Date;
  type: EntryType;
  title: string;
  detail?: string;
};

const ICONS: Record<EntryType, React.ReactNode> = {
  vorgang: <Briefcase20Regular />,
  todo: <Note20Regular />,
  termin: <Calendar20Regular />,
  mail: <Mail20Regular />,
  anruf: <Call20Regular />,
  zusammenfuehrung: <PeopleSwap20Regular />,
  verantwortlichkeit: <PeopleSwap20Regular />,
};

const TYPE_LABELS: Record<EntryType, string> = {
  vorgang: "Vorgang",
  todo: "Todo",
  termin: "Termin",
  mail: "Schriftverkehr",
  anruf: "Anruf",
  zusammenfuehrung: "Zusammenführung",
  verantwortlichkeit: "Verantwortlichkeit",
};

const TYPE_COLORS: Record<EntryType, string> = {
  vorgang: "#6d4c9e",
  todo: "#2e7d32",
  termin: "#1976d2",
  mail: "#c2185b",
  anruf: "#ef6c00",
  zusammenfuehrung: "#00838f",
  verantwortlichkeit: "#8d6e63",
};

const safeDate = (raw?: string | null): Date | null => {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmt = (d: Date) =>
  d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

type MergeHistoryDto = {
  id: number;
  loserContactId: number;
  loserName?: string | null;
  loserContactNbr?: string | null;
  mergedByName?: string | null;
  mergedAt?: string | null;
};

type OwnershipChange = { old: string | null; new: string | null };

type OwnershipHistoryDto = {
  id: number;
  at: string;
  by: string | null;
  fo?: OwnershipChange;
  boType?: OwnershipChange;
  boTeamId?: OwnershipChange;
  boUserId?: OwnershipChange;
};

type Props = {
  contactId: number;
};

const KundenTagebuch = ({ contactId }: Props) => {
  const { users, appTeams } = useReferenceData();
  const [vorgaenge, setVorgaenge] = useState<VorgangDto[]>([]);
  const [todos, setTodos] = useState<TodoDto[]>([]);
  const [merges, setMerges] = useState<MergeHistoryDto[]>([]);
  const [ownership, setOwnership] = useState<OwnershipHistoryDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const hasId = Number.isFinite(contactId) && contactId > 0;
    const mergeHistory = hasId
      ? apiFetch<MergeHistoryDto[]>(
          `duplicate-candidates/${contactId}/merge-history`,
        )
          .then((r) => r.data)
          .catch(() => [] as MergeHistoryDto[])
      : Promise.resolve([] as MergeHistoryDto[]);
    const ownershipHistory = hasId
      ? apiFetch<OwnershipHistoryDto[]>(`contact/${contactId}/ownership-history`)
          .then((r) => r.data)
          .catch(() => [] as OwnershipHistoryDto[])
      : Promise.resolve([] as OwnershipHistoryDto[]);
    Promise.all([
      vorgangService.getAllVorgaenge().catch(() => [] as VorgangDto[]),
      vorgangService.getAllTodos().catch(() => [] as TodoDto[]),
      mergeHistory,
      ownershipHistory,
    ]).then(([vs, ts, ms, os]) => {
      if (cancelled) return;
      setVorgaenge(vs);
      setTodos(ts);
      setMerges(ms);
      setOwnership(os);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [contactId]);

  const entries = useMemo<Entry[]>(() => {
    const out: Entry[] = [];

    // Namensauflösung über Reference-Data (User-/Team-IDs → Anzeigenamen).
    const userMap = new Map(users.map((u) => [u.id, u.name] as const));
    const teamMap = new Map(appTeams.map((t) => [t.id, t.name] as const));
    const userName = (id: string | null) => (id ? userMap.get(id) ?? "Unbekannt" : "—");
    const teamName = (id: string | null) => (id ? teamMap.get(id) ?? "Unbekanntes Team" : "—");
    const boTypeLabel = (v: string | null) =>
      v === "TEAM" ? "Team" : v === "USER" ? "Person" : v === "NONE" ? "Keiner" : v ?? "—";

    for (const v of vorgaenge) {
      if (v.kunde?.id !== contactId) continue;
      const d = safeDate(v.frist) ?? new Date();
      out.push({
        id: `vorgang-${v.id}`,
        date: d,
        type: "vorgang",
        title: v.titel,
        detail: v.beschreibung,
      });
    }

    for (const t of todos) {
      const linked =
        t.linkedEntityType === "KUNDE" && t.linkedEntityId === contactId;
      const legacy = t.assignedContacts?.some((c) => c.id === contactId) ?? false;
      if (!linked && !legacy) continue;
      const d = safeDate(t.frist) ?? safeDate(t.twv) ?? new Date();
      out.push({
        id: `todo-${t.id}`,
        date: d,
        type: "todo",
        title: t.titel,
        detail: t.beschreibung || `Status: ${t.status}`,
      });
    }

    for (const m of merges) {
      const name = m.loserName?.trim() || `Kontakt #${m.loserContactId}`;
      const nbr = m.loserContactNbr ? ` (Nr. ${m.loserContactNbr})` : "";
      const who = m.mergedByName ? ` durch ${m.mergedByName}` : "";
      out.push({
        id: `merge-${m.id}`,
        date: safeDate(m.mergedAt) ?? new Date(),
        type: "zusammenfuehrung",
        title: `Kontakt „${name}"${nbr} zusammengeführt`,
        detail: `Der Kontakt „${name}" wurde als Duplikat in diesen Kontakt zusammengeführt${who}. Seine Daten wurden übernommen und archiviert.`,
      });
    }

    for (const o of ownership) {
      const parts: string[] = [];
      if (o.fo) parts.push(`FO: ${userName(o.fo.old)} → ${userName(o.fo.new)}`);
      if (o.boTeamId)
        parts.push(`BO-Team: ${teamName(o.boTeamId.old)} → ${teamName(o.boTeamId.new)}`);
      if (o.boUserId)
        parts.push(`BO-Person: ${userName(o.boUserId.old)} → ${userName(o.boUserId.new)}`);
      if (!o.boTeamId && !o.boUserId && o.boType)
        parts.push(`BO-Typ: ${boTypeLabel(o.boType.old)} → ${boTypeLabel(o.boType.new)}`);
      out.push({
        id: `ownership-${o.id}`,
        date: safeDate(o.at) ?? new Date(),
        type: "verantwortlichkeit",
        title: parts.length ? parts.join("; ") : "Verantwortlichkeit geändert",
        detail: o.by ? `Geändert durch ${userName(o.by)}` : undefined,
      });
    }

    return out.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [vorgaenge, todos, merges, ownership, users, appTeams, contactId]);

  return (
    <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: 12 }}>
      {loading ? (
        <div style={{ color: "#757575", fontSize: 13 }}>Tagebuch wird geladen…</div>
      ) : entries.length === 0 ? (
        <div style={{ color: "#757575", fontSize: 13 }}>
          Keine Aktivitäten für diesen Kontakt erfasst.
        </div>
      ) : (
        entries.map((e) => (
          <div
            key={e.id}
            style={{
              display: "grid",
              gridTemplateColumns: "32px 160px minmax(0, 1fr)",
              gap: 12,
              alignItems: "start",
              backgroundColor: "#fff",
              border: "1px solid #ececec",
              borderRadius: 8,
              padding: "12px 14px",
            }}
          >
            <span style={{ color: TYPE_COLORS[e.type] }}>{ICONS[e.type]}</span>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{fmt(e.date)}</span>
              <span style={{ fontSize: 11, color: TYPE_COLORS[e.type], fontWeight: 600 }}>
                {TYPE_LABELS[e.type]}
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#2d142b" }}>{e.title}</span>
              {e.detail && (
                <span style={{ fontSize: 13, color: "#5b5560", whiteSpace: "pre-wrap" }}>
                  {e.detail}
                </span>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default KundenTagebuch;
